/**
 * Что человек уже видел.
 *
 * Одна отметка времени: всё, что случилось позже, — непрочитанное.
 * Список прочитанных идентификаторов был бы точнее, но растёт без
 * предела и требует уборки; отметка времени отвечает на тот же вопрос
 * одним числом.
 *
 * Живёт в профиле, а браузер держит копию на время загрузки. Раньше
 * отметка была только в браузере, и прочитанное на компьютере на
 * телефоне продолжало гореть новым — одна учётная запись показывала
 * два разных состояния. Теперь прочитанное гаснет везде.
 */

import { useEffect, useState } from 'react';

const KEY = 'specter.seen.notifications.v1';

const listeners = new Set<(at: number) => void>();

let current = read();

function read(): number {
  try {
    const raw = localStorage.getItem(KEY);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

/**
 * Куда записать отметку в профиль. Ставит склад терминала, когда
 * загрузится: сам он тяжёлый и в первый экран не ввозится.
 */
let persist: ((at: number) => void) | null = null;
let unsaved = 0;

export function setSeenPersister(write: (at: number) => void): void {
  persist = write;
  if (unsaved > 0) {
    write(unsaved);
    unsaved = 0;
  }
}

/**
 * Отметка пришла из профиля — с другого устройства.
 *
 * Берётся, только если она новее своей: прочитанное на телефоне гаснет
 * и на компьютере, а обратно отметка не откатывается.
 */
export function adoptSeen(at: number): void {
  if (at <= current) return;
  current = at;
  try {
    localStorage.setItem(KEY, String(at));
  } catch {
    // Приватное окно: отметка живёт до перезагрузки.
  }
  for (const listener of listeners) listener(at);
}

/** Отметить всё до этого момента прочитанным. */
export function markSeen(at: number = Date.now()): void {
  /*
   * Отметка только вперёд.
   *
   * Открыв страницу второй раз, человек не должен вернуть себе
   * непрочитанное, которое уже посмотрел: два окна с разным временем
   * иначе перетирали бы отметку друг друга по кругу.
   */
  if (at <= current) return;

  current = at;
  try {
    localStorage.setItem(KEY, String(at));
  } catch {
    // Приватное окно: отметка живёт до перезагрузки. Это лучше, чем
    // отказаться отмечать вовсе.
  }

  for (const listener of listeners) listener(at);
  // И в профиль — чтобы прочитанное погасло на всех устройствах.
  if (persist) persist(at);
  else unsaved = at;
}

export function seenAt(): number {
  return current;
}

export function useSeenAt(): number {
  const [at, setAt] = useState(current);

  useEffect(() => {
    listeners.add(setAt);
    setAt(current);
    return () => {
      listeners.delete(setAt);
    };
  }, []);

  return at;
}

/** Сколько из этих событий человек ещё не видел. */
export function unreadCount(times: readonly number[], at: number): number {
  return times.reduce((count, time) => (time > at ? count + 1 : count), 0);
}
